/**
 * Symbol → icon resolution. Pure: no DOM, no stores.
 *
 * Runs once per symbol (results are memoised per manifest + scope), never in a render path.
 */

import {
  DEFAULT_SYMBOL_ICON_MANIFEST,
  type SymbolIconAlias,
  type SymbolIconClass,
  type SymbolIconManifest,
  type SymbolIconRule,
} from "./manifest";
import { SYMBOL_ICON_KEYS } from "./keys.generated";
import { normaliseBrandName } from "./brand-name";

/** The fields of a terminal symbol the rules may look at. */
export interface SymbolIconInput {
  /** Symbol name as the broker spells it (`Symbol.currency`). */
  name: string;
  /** Group path (`Symbol.groupName`), e.g. `Cryptos\BAT`. */
  group?: string;
  description?: string;
}

export interface ResolvedSymbolIcon {
  cls: SymbolIconClass;
  /** Library key of the main icon; null → draw the monogram. */
  base: string | null;
  /** Library key of the small quote badge, when the symbol is a pair. */
  quote: string | null;
  /** 1–2 characters for the monogram fallback. */
  monogram: string;
  /** Rule or alias that decided, for the admin test box and coverage reports. */
  via: string;
}

interface CompiledRule {
  rule: SymbolIconRule;
  re: RegExp;
  group?: RegExp;
  description?: RegExp;
}

interface Compiled {
  manifest: SymbolIconManifest;
  rules: CompiledRule[];
  cache: Map<string, ResolvedSymbolIcon>;
}

const KEYS: ReadonlySet<string> = new Set(SYMBOL_ICON_KEYS);
const FUTURES_MONTH = /_[FGHJKMNQUVXZ]\d{2}$/;
const BROKER_SUFFIX = /(?:[._\-+!^][A-Za-z0-9]{1,6})$/;

const compiledByManifest = new WeakMap<SymbolIconManifest, Compiled>();

function compile(manifest: SymbolIconManifest): Compiled {
  let compiled = compiledByManifest.get(manifest);
  if (compiled) return compiled;
  const rules: CompiledRule[] = [];
  for (const rule of manifest.rules) {
    try {
      rules.push({
        rule,
        re: new RegExp(rule.pattern, rule.flags),
        group: rule.where?.group ? new RegExp(rule.where.group, "i") : undefined,
        description: rule.where?.description ? new RegExp(rule.where.description, "i") : undefined,
      });
    } catch {
      // A rule that does not compile is skipped, never fatal: one bad publish must not blank every icon.
    }
  }
  compiled = { manifest, rules, cache: new Map() };
  compiledByManifest.set(manifest, compiled);
  return compiled;
}

function monogramOf(name: string): string {
  const letters = name.replace(/^[^A-Za-z0-9]+/, "").match(/[A-Za-z0-9]/g);
  if (!letters || letters.length === 0) return "?";
  return (letters[0] + (letters[1] ?? "")).toUpperCase();
}

/** `asset:EUR` → `flag:eu`; null when the library has no such icon. */
function resolveKey(template: string, match: RegExpExecArray | null, manifest: SymbolIconManifest): string | null {
  const filled = match ? template.replace(/\$(\d)/g, (_, n: string) => match[Number(n)] ?? "") : template;
  const sep = filled.indexOf(":");
  if (sep < 0) return null;
  const ns = filled.slice(0, sep);
  const code = filled.slice(sep + 1);
  if (!code) return null;

  if (ns === "asset" || ns === "crypto") {
    const upper = code.toUpperCase();
    const mapped = manifest.assets[upper];
    if (mapped && (ns === "asset" || mapped.startsWith("crypto:"))) return KEYS.has(mapped) ? mapped : null;
    if (ns === "asset") {
      const cc = manifest.currencies[upper];
      if (cc) return KEYS.has(`flag:${cc}`) ? `flag:${cc}` : null;
    }
    const coin = `crypto:${upper}`;
    return KEYS.has(coin) ? coin : null;
  }
  return KEYS.has(filled) ? filled : null;
}

/** A share's logo: by market + ticker when the rule captured both, else by company name. */
function stockLogo(
  rule: SymbolIconRule,
  fill: (template: string) => string,
  input: SymbolIconInput,
  manifest: SymbolIconManifest,
): string | null {
  const { markets, tickers, names } = manifest.stocks;
  const market = rule.exchange ? markets[fill(rule.exchange)] : undefined;
  const byTicker = market && rule.ticker ? tickers[`${market}:${fill(rule.ticker)}`] : undefined;
  const slug = byTicker ?? (input.description ? names[normaliseBrandName(input.description)] : undefined);
  return slug && KEYS.has(`stock:${slug}`) ? `stock:${slug}` : null;
}

function fromAlias(
  alias: SymbolIconAlias,
  via: string,
  name: string,
  manifest: SymbolIconManifest,
): ResolvedSymbolIcon | null {
  const base = resolveKey(alias.base, null, manifest);
  if (!base) return null;
  const quote = alias.quote ? resolveKey(alias.quote, null, manifest) : null;
  return { cls: alias.cls, base, quote, monogram: monogramOf(name), via };
}

/** Class implied by what the base icon is — `pair-3x3` also catches XAUUSD and BTCUSD. */
function classOf(rule: SymbolIconRule, base: string): SymbolIconClass {
  if (rule.cls !== "fx") return rule.cls;
  if (base.startsWith("crypto:")) return "crypto";
  if (base === "cmd:oil" || base === "cmd:gas") return "energy";
  if (base.startsWith("cmd:")) return "metal";
  return "fx";
}

function resolveUncached(input: SymbolIconInput, compiled: Compiled): ResolvedSymbolIcon {
  const { manifest } = compiled;
  const name = input.name.trim();
  const monogram = monogramOf(name);

  // 1. Whole-name aliases, as spelled.
  const upper = name.toUpperCase();
  const bare = upper.replace(/^[#.]+/, "").replace(FUTURES_MONTH, "");
  const alias = (candidate: string) => {
    const entry = manifest.aliases[candidate];
    return entry ? fromAlias(entry, `alias:${candidate}`, name, manifest) : null;
  };
  const exact = alias(upper) ?? (bare !== upper ? alias(bare) : null);
  if (exact) return exact;

  // 2. Ordered rules; the first whose icon actually exists wins.
  const applyRules = (fallback: boolean): ResolvedSymbolIcon | null => {
    for (const { rule, re, group, description } of compiled.rules) {
      if (Boolean(rule.fallback) !== fallback) continue;
      if (group && !group.test(input.group ?? "")) continue;
      if (description && !description.test(input.description ?? "")) continue;
      const match = re.exec(name);
      if (!match) continue;

      if (rule.base.startsWith("mono:")) {
        const fill = (t: string) => t.replace(/\$(\d)/g, (_, n: string) => match[Number(n)] ?? "");
        const text = fill(rule.base.slice(5));
        const logo = rule.cls === "stock" ? stockLogo(rule, fill, input, manifest) : null;
        return { cls: rule.cls, base: logo, quote: null, monogram: monogramOf(text || name), via: `rule:${rule.id}` };
      }

      const base = resolveKey(rule.base, match, manifest);
      if (!base) continue;
      const quote = rule.quote ? resolveKey(rule.quote, match, manifest) : null;
      if (rule.quoteRequired && !quote) continue;
      return { cls: classOf(rule, base), base, quote, monogram, via: `rule:${rule.id}` };
    }
    return null;
  };
  const ruled = applyRules(false);
  if (ruled) return ruled;

  // 3. The alias again with a broker suffix peeled off (GOLD.pro, US500.cash), then the
  //    group-based last resorts.
  const stripped = bare.replace(BROKER_SUFFIX, "");
  const late = (stripped !== bare ? alias(stripped) : null) ?? applyRules(true);
  if (late) return late;

  return { cls: "other", base: null, quote: null, monogram, via: "fallback" };
}

/**
 * Resolve a symbol to its icon.
 *
 * @param scope Gateway slug. The same name can mean different things at two brokers, so results
 *              are cached per scope; per-gateway rule overrides will hang off it.
 */
export function resolveSymbolIcon(
  input: SymbolIconInput,
  scope = "",
  manifest: SymbolIconManifest = DEFAULT_SYMBOL_ICON_MANIFEST,
): ResolvedSymbolIcon {
  const compiled = compile(manifest);
  const cacheKey = `${scope}\u0000${input.name}\u0000${input.group ?? ""}\u0000${input.description ?? ""}`;
  let hit = compiled.cache.get(cacheKey);
  if (!hit) {
    hit = resolveUncached(input, compiled);
    compiled.cache.set(cacheKey, hit);
  }
  return hit;
}
